import React, { useMemo } from 'react';

/** Short quotes for loading screens — kept professional and inclusive. */
const MOTIVATIONAL_QUOTES = [
  'Small steps still move you forward.',
  'Progress beats perfection.',
  'Momentum builds one action at a time.',
  'Focus on the next right thing.',
  'Stay curious—good things take shape.',
  'Clear and steady wins the long game.',
  'You are allowed to grow at your own pace.',
  'Patience is part of the process.',
  'Show up; the rest follows.',
  'Every expert was once a beginner.',
  'Ship, learn, improve.',
  'Quiet effort adds up.',
] as const;

function pickQuote(): string {
  const i = Math.floor(Math.random() * MOTIVATIONAL_QUOTES.length);
  return MOTIVATIONAL_QUOTES[i];
}

export interface LoadingAnimationProps {
  /**
   * If set, shown instead of a motivational quote (e.g. inline loaders that need a specific label).
   */
  message?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const LoadingAnimation: React.FC<LoadingAnimationProps> = ({
  message,
  size = 'md',
  className = '',
}) => {
  const quote = useMemo(() => pickQuote(), []);
  const display = message ?? quote;

  const spinner = {
    sm: 'h-4 w-4 border-[1.5px]',
    md: 'h-5 w-5 border-2',
    lg: 'h-6 w-6 border-2',
  }[size];

  const text = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  }[size];

  return (
    <div
      className={`flex flex-row items-center justify-center gap-3 ${className}`}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div
        className={`shrink-0 rounded-full border-gray-200 border-t-gray-800 border-l-gray-800 motion-reduce:animate-none dark:border-neutral-600 dark:border-t-neutral-200 dark:border-l-neutral-200 ${spinner} animate-spin`}
        aria-hidden
      />
      <p
        className={`max-w-md font-sans font-normal text-gray-500 dark:text-gray-400 ${text}`}
      >
        {display}
      </p>
      <span className="sr-only">Loading. Please wait.</span>
    </div>
  );
};

export default LoadingAnimation;
